import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useThemeMode } from "../components/context/ThemeModeContext";
import { useCurrentUser } from "../components/context/UserContext";
import { herramientasDe, nombreVisible, iniciales } from "../components/navbars/herramientas";
import useWeeklyUsage from "../hooks/useWeeklyUsage";

/**
 * Home v4 — landing pública (#1439, opción E) para usuarios logueados y no logueados.
 *
 * Es `public/home_v4.html` (HTML + CSS + JS inline, assets en `public/assets/landing/`).
 * En "/" y "/home" no se dibuja la DxNavbar (DxNavbar.js solo deja su popup de Ajustes, que
 * abre el menú de cuenta de la v4): manda la barra propia de la v4, que
 * recibe la sesión y las herramientas por postMessage (efecto de abajo). La home v3 ya NO se publica
 * (#1681, 27-oct-2026: mostraba diagnósticos diferenciales): su respaldo está en `frontend/respaldo/home_v3.html`.
 * Para volver a ella hay que copiarla a `public/`, quitar sus 301 de `public/_redirects`, quitar el test #1681
 * de `src/perf-1717-headers.test.js` y apuntar el src de abajo.
 *
 * El Home.js legacy (1017 líneas, MUI + scroll-snap) está en `Home.legacy.js`.
 *
 * Theme sync: el iframe tiene su propio CSS y no hereda las vars del parent.
 * Pasamos el modo inicial via `?theme=` (sin flash) y para cambios en vivo
 * hacemos `postMessage({type:"smartdx-theme",mode})` al iframe — escuchado en home_v4.html.
 */
// Cache buster — el mockup se actualiza frecuentemente y el browser cachea fuerte
// la carga del iframe. Cambiamos esta constante (o usamos build time) para forzar reload.
const HOME_VERSION = "v4-2026-10-02";

export default function Home() {
  const iframeRef = useRef(null);
  const { mode } = useThemeMode();
  const location = useLocation();
  // Modo INICIAL capturado una sola vez (al montar). El src del iframe usa este
  // valor fijo para la carga sin flash; los cambios EN VIVO van por postMessage
  // (efecto de abajo) y NO recargan el iframe — así no se pierde la posición
  // de scroll al togglear el tema (no vuelve al hero).
  const initialModeRef = useRef(mode);

  // Asegurar que el body no scrollee — el iframe ya ocupa 100vh.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Propaga cambios de modo al iframe sin recargarlo.
  useEffect(() => {
    const post = () => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: "smartdx-theme", mode },
          "*",
        );
      }
    };
    post();
    // Reintenta al cargar el iframe — si el primer post llegó antes del load
    // del documento interno, no había listener.
    const el = iframeRef.current;
    el?.addEventListener("load", post);
    return () => el?.removeEventListener("load", post);
  }, [mode]);

  // Sesión y herramientas para la barra de v4: en "/" no hay DxNavbar, así que la landing muestra
  // "Ir a mi Turno" y las herramientas permitidas en su panel. Solo datos de la cuenta (nombre,
  // iniciales, correo y si es admin, para el menú de cuenta D1 de las iniciales): nada clínico.
  // El uso de la semana (#1439, decisión de Sebastián 03-oct) sale del mismo hook que la píldora
  // de la DxNavbar, que en la home no se dibuja: la landing solo lo muestra, no calcula nada.
  const { currentUser } = useCurrentUser();
  const uso = useWeeklyUsage(Boolean(currentUser));
  useEffect(() => {
    const el = iframeRef.current;
    const nombre = nombreVisible(currentUser);
    const msg = {
      type: "smartdx-session",
      user: currentUser
        ? {
            nombre,
            iniciales: iniciales(nombre),
            correo: currentUser.email || "",
            admin: Boolean(currentUser.is_superuser),
            uso: uso ? { documentos: uso.documentos, limite: uso.limite_semanal ?? null, nivel: uso.nivel ?? null } : null,
          }
        : null,
      tools: currentUser ? herramientasDe(currentUser).map(({ short, sub, path }) => ({ short, sub: sub || "", path })) : [],
    };
    const post = () => el?.contentWindow?.postMessage(msg, window.location.origin);
    post();
    el?.addEventListener("load", post);
    return () => el?.removeEventListener("load", post);
  }, [currentUser, uso]);

  // Reenvía la navegación de secciones al iframe. Los links de secciones (la
  // barra del acceso, BarraAccesoV4, y las chips "Explorar" de la DxNavbar en
  // las demás pantallas) hacen navigate("/#informes");
  // React Router actualiza el hash (vía pushState, que NO dispara 'hashchange'),
  // por eso reaccionamos a location.hash. home_v4.html traduce el id (goTo) y
  // hace scrollIntoView.
  useEffect(() => {
    const section = (location.hash || "").replace("#", "");
    if (!section) return;
    const post = () => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "smartdx-navigate", section },
        "*",
      );
    };
    post();
    // Si venimos desde otra ruta, el iframe puede no haber cargado aún.
    const el = iframeRef.current;
    el?.addEventListener("load", post);
    return () => el?.removeEventListener("load", post);
  }, [location.hash]);

  return (
    <iframe
      ref={iframeRef}
      src={`/home_v4.html?v=${HOME_VERSION}&theme=${initialModeRef.current}`}
      title="SmartDx — Home"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        border: 0,
        zIndex: 1,
        background: "var(--bg-base)",
      }}
    />
  );
}
